/**
 * Image utilities — resize + base64 conversion for upload to Claude Vision.
 *
 * Claude accepts images up to 8000×8000 px and 5 MB. To keep upload latency
 * low and cost predictable we:
 *   - Downscale so the longest side ≤ MAX_DIM (default 1280px).
 *   - Re-encode as JPEG q=0.85 (smaller than PNG for photos).
 *   - Strip the `data:image/jpeg;base64,` prefix before returning the data.
 *
 * The output shape matches what the Cloud Function (`askAutoMeAI` /
 * `analyzeFine`) expects in its `image` / `images` payload field.
 */

const DEFAULT_MAX_DIM = 1280;
const DEFAULT_QUALITY = 0.85;
const FALLBACK_MIME = 'image/jpeg';

/**
 * @typedef {Object} PreparedImage
 * @property {string} mediaType  e.g. 'image/jpeg'
 * @property {string} data       Base64 without the `data:...;base64,` prefix
 * @property {string} previewUrl A data: URL suitable for an `<img src>`
 * @property {number} byteLength Approx size after re-encoding
 */

/**
 * Convert a File/Blob into a downscaled JPEG ready to ship to the API.
 *
 * @param {File | Blob} file
 * @param {{ maxDim?: number; quality?: number }} [opts]
 * @returns {Promise<PreparedImage>}
 */
export async function prepareImage(file, opts = {}) {
  const maxDim = opts.maxDim ?? DEFAULT_MAX_DIM;
  const quality = opts.quality ?? DEFAULT_QUALITY;

  const dataUrl = await readAsDataURL(file);
  const img = await loadImage(dataUrl);

  // Determine target size — never upscale.
  const scale = Math.min(maxDim / img.naturalWidth, maxDim / img.naturalHeight, 1);
  const targetW = Math.round(img.naturalWidth * scale);
  const targetH = Math.round(img.naturalHeight * scale);

  // Canvas re-encode → always JPEG (smaller than PNG for photos).
  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');
  ctx.drawImage(img, 0, 0, targetW, targetH);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
      FALLBACK_MIME,
      quality,
    );
  });

  const previewUrl = await readAsDataURL(blob);
  const base64 = previewUrl.split(',')[1] || '';

  return {
    mediaType: FALLBACK_MIME,
    data: base64,
    previewUrl,
    byteLength: blob.size,
  };
}

function readAsDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
}

function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image load failed'));
    img.src = dataUrl;
  });
}
